/* eslint-disable max-lines-per-function */
import { useCallback } from 'react';

import { translationKey, type TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { useRecommendationsJob } from 'src/app/pages/startAPetitionV2/shared/hooks';

import { buildSchema } from './schema';

type UseIssuePromptReturn = ModelHookResult<
	{
		schema: ReturnType<typeof buildSchema>;
		defaultValues: { issue: string };
		descriptionKey: TranslationKey;
		loading: boolean;
		error: boolean;
	},
	{
		submit: (data: { issue: string }) => void;
		back: () => void;
		persistDraft: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
		handleError: () => void;
	}
>;

const DESCRIPTION_KEYS = {
	recommendations_draft: translationKey('corgi.start_a_petition.issue_prompt.description_recommendations'),
	default: translationKey('corgi.start_a_petition.issue_prompt.description'),
} as const;

function getDescriptionKey(draftType: string | undefined | null): TranslationKey {
	return draftType === 'recommendations_draft' ? DESCRIPTION_KEYS.recommendations_draft : DESCRIPTION_KEYS.default;
}

export function useIssuePrompt(): UseIssuePromptReturn {
	const { actions, data } = useStartAPetitionPageState();
	const track = useTracking();
	const { translate } = useI18n();
	const { draft } = data;

	const experimentVariant = draft?.metadata?.variant;
	const recommendationsJobId = draft?.metadata?.recommendationsJobId;

	const { mutate, status } = useRecommendationsJob({
		jobId: recommendationsJobId,
		onSuccess: (jobResult) => {
			if (jobResult.status !== 'COMPLETED') return;

			actions.send({
				type: 'submit',
				draft: {
					input: {
						recommendations: jobResult.recommendations,
					},
					metadata: { recommendationsJobId: null },
				},
			});
		},
		onError: (error) => {
			void track('sap_recommendations_job_error', { error: error.constructor.name });
		},
	});

	const submit = useCallback(
		(values: { issue: string }) => {
			void track('sap_issue_submit', { prompt: values.issue });

			if (experimentVariant === 'recommendations_variant_b') {
				mutate({ mainPrompt: values.issue, personalStory: '' });
			} else {
				actions.send({
					type: 'submit',
					draft: { input: { issuePrompt: values.issue } },
				});
			}
		},
		[actions, track, experimentVariant, mutate],
	);

	const back = useCallback(() => {
		void track('sap_issue_back');
		actions.send({ type: 'back', target: null });
	}, [actions, track]);

	const persistDraft = useCallback(
		(event: React.FocusEvent<HTMLTextAreaElement>) => {
			actions.send({ type: 'submit', draft: { input: { issuePrompt: event.target.value.trim() } }, target: 'self' });
		},
		[actions],
	);

	const handleError = useCallback(() => {
		actions.send({ type: 'error' });
	}, [actions]);

	return {
		data: {
			schema: buildSchema({ translate }),
			defaultValues: { issue: draft?.input?.issuePrompt ?? '' },
			descriptionKey: getDescriptionKey(draft?.type),
			loading: status === 'pending',
			error: status === 'error',
		},
		actions: { submit, back, persistDraft, handleError },
	};
}
